import { useEffect, useRef, useState } from 'react';
import { cleanPreferences, readPreferences, writePreferences, preferenceTypes, homeKey } from '../services/assistantDiscovery';
import { selectedPropertyContext } from '../services/assistantApi';
export default function useAssistantDiscovery({
  favorites,
  messagesRef,
  setMessages,
  loadHome,
  closeProperty
}) {
  const [profile, setProfile] = useState(readPreferences),
    [preferencesOpen, setPreferencesOpen] = useState(false),
    [homes, setHomes] = useState([]),
    [comparison, setComparison] = useState([]),
    [comparisonOpen, setComparisonOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [answer, setAnswer] = useState('');
  const live = useRef({}),
    generation = useRef(0);
  live.current = {
    profile,
    homes,
    comparison,
    comparisonOpen,
    busy
  };
  useEffect(() => {
    const valid = new Set([...favorites].map(String));
    setMessages(previous => {
      let changed = false;
      const next = previous.map(message => {
        if (!message.isFavorites) return message;
        const rows = message.properties.filter(home => valid.has(homeKey(home)));
        if (rows.length === message.properties.length) return message;
        changed = true;
        return {
          ...message,
          properties: rows,
          currentPage: Math.min(message.currentPage || 0, Math.max(0, Math.ceil(rows.length / 6) - 1))
        };
      });
      return changed ? next : previous;
    });
  }, [favorites, setMessages]);
  const save = value => {
    const next = writePreferences(value);
    live.current.profile = next;
    setProfile(next);
    return next;
  };
  const select = next => {
    generation.current++;
    live.current.homes = next;
    setHomes(next);
    setComparison([]);
    setComparisonOpen(false);
    setAnswer('');
  };
  const closeComparison = () => { generation.current++; live.current.comparisonOpen = false; setComparisonOpen(false); };
  const remove = key => select(live.current.homes.filter(h => homeKey(h) !== key));
  const add = selected => {
    const next = [...live.current.homes];
    for (const home of selected) {
      if (!home || next.some(h => homeKey(h) === homeKey(home))) continue;
      if (next.length === 3) return 'Your comparison tray holds three homes. Remove one before adding another.';
      next.push(home);
    }
    select(next);
    return `${next.length} homes selected for comparison. Selections stay with you across pages.`;
  };
  async function compare() {
    if (live.current.busy) return 'Details are already loading.';
    if (live.current.homes.length < 2) return 'Select at least two homes to compare.';
    const version = ++generation.current;
    live.current.busy = true;
    setBusy(true);
    setAnswer('');
    try {
      const details = await Promise.all(live.current.homes.map(h => loadHome(h, true)));
      if (version !== generation.current) return 'The comparison selection changed.';
      if (details.some(h => !h)) throw new Error('One of these homes could not be loaded. Please refresh or remove it.');
      setComparison(details);
      live.current.comparison = details;
      live.current.comparisonOpen = true;
      setComparisonOpen(true);
      closeProperty();
      return 'The comparison is open. Homes are labeled A, B, and C in selection order. Ask about their differences or how they fit your preferences.';
    } catch (e) {
      setAnswer(e.message);
      return e.message;
    } finally {
      live.current.busy = false;
      setBusy(false);
    }
  }
  async function showFavorites() {
    if (live.current.busy) return 'Homes are already loading.';
    if (!favorites.size) return 'You have no saved homes yet. Ask me to save a property, or use Save on its tile first.';
    const version = ++generation.current;
    live.current.busy = true;
    setBusy(true);
    try {
      const keys = [...favorites];
      const rows = [];
      for (let i = 0; i < keys.length; i += 6) {
        const batch = await Promise.all(keys.slice(i, i + 6).map(key => loadHome({
          listing_key: key
        }, false).catch(() => null)));
        rows.push(...batch.filter(Boolean));
        if (version !== generation.current) return 'The selection changed.';
      }
      if (!rows.length) return 'Saved homes could not be loaded. Your saved selections are still preserved.';
      setComparisonOpen(false); live.current.comparisonOpen = false;
      setPreferencesOpen(false);
      setMessages(prev => [...prev, {
        type: 'bot',
        content: 'properties',
        properties: rows,
        currentPage: 0,
        hasMore: false,
        isFavorites: true,
        searchId: `favorites-${Date.now()}`
      }]);
      closeProperty();
      return `Showing ${rows.length} saved homes, six per page, numbered one through six.${rows.length < keys.length ? ' Some saved homes are temporarily unavailable.' : ''}`;
    } finally {
      live.current.busy = false;
      setBusy(false);
    }
  }
  async function run(params, page) {
    const {
      command
    } = params;
    if (command === 'show_favorites') return showFavorites();
    if (command === 'show_results') {
      generation.current++;
      setComparisonOpen(false); live.current.comparisonOpen = false;
      setPreferencesOpen(false);
      closeProperty();
      const latest = [...messagesRef.current].reverse().find(m => m.content === 'properties');
      const search = [...messagesRef.current].reverse().find(m => m.content === 'properties' && !m.isFavorites);
      if (!search) return latest ? 'Back to your saved homes. Ask me to search for more properties.' : 'Tell me where you would like to search for homes.';
      if (latest !== search) setMessages(prev => [...prev, {...search, searchId:`results-${Date.now()}`}]);
      return 'Back to your search results, on the same page.';
    }
    if (command === 'close_preferences') {
      setPreferencesOpen(false);
      return 'Preferences closed.';
    }
    if (command === 'show_preferences') {
      setPreferencesOpen(true);
      return 'Your home preferences are open. You can edit or forget them here.';
    }
    if (command === 'clear_preferences') {
      save(cleanPreferences());
      return 'Your home preferences have been cleared from this browser.';
    }
    if (['remember_preference', 'forget_preference'].includes(command)) {
      const type = params.preference_type,
        text = params.text?.trim();
      if (!preferenceTypes.includes(type) || !text) return 'Please specify the preference to remember or forget.';
      const next = {
        ...live.current.profile
      };
      next[type] = next[type].filter(item => item.toLowerCase() !== text.toLowerCase());
      if (command === 'remember_preference') next[type].push(text);
      save(next);
      return command === 'remember_preference' ? `Remembered: ${text}. You can edit this in Home preferences.` : `Removed: ${text}.`;
    }
    if (command === 'clear_compare') {
      select([]);
      return 'Comparison selections cleared.';
    }
    if (command === 'remove_compare') {
      const home = live.current.homes[{
        A: 0,
        B: 1,
        C: 2
      }[params.comparison_slot]];
      if (!home) return 'Choose comparison A, B, or C from the tray.';
      remove(homeKey(home));
      return 'Home removed from comparison.';
    }
    if (['add_compare', 'compare_properties'].includes(command)) {
      const numbers = params.numbers || (params.number != null ? [params.number] : []);
      if (numbers.some(n => !Number.isInteger(n) || !page[n - 1])) return 'Choose property numbers from the current page.';
      if (numbers.length) {
        const message = add(numbers.map(n => page[n - 1]));
        if (command === 'add_compare' || message.startsWith('Your comparison')) return message;
      }
      if (command === 'add_compare') return 'Which property numbers would you like to compare?';
      return compare();
    }
    if (command === 'close_compare') {
      closeComparison();
      return 'Comparison closed. Your selected homes remain in the tray.';
    }
    return null;
  }
  return {
    profile,
    preferencesOpen,
    setPreferencesOpen,
    save,
    homes,
    remove,
    clear: () => select([]),
    add,
    compare,
    comparison,
    comparisonOpen,
    setComparisonOpen: value => { if (!value) closeComparison(); else { live.current.comparisonOpen = true; setComparisonOpen(true); } },
    busy,
    answer,
    setAnswer,
    showFavorites,
    run,
    context: () => ({
      preferences_open: preferencesOpen,
      preferences: live.current.profile,
      comparison_open: live.current.comparisonOpen,
      comparison_properties: live.current.comparisonOpen ? live.current.comparison.map(h => selectedPropertyContext(h)) : [],
      comparison_count: live.current.homes.length,
      comparison_labels: live.current.homes.map((h, i) => ({
        slot: String.fromCharCode(65 + i),
        listing_key: homeKey(h)
      }))
    })
  };
}
